import React, { useEffect, useState, useCallback } from "react";
import axiosClient from "../../../api/axiosClient";
import { useAuth } from "../../../context/AuthContext";
import UserFormModal from "./UserFormModal";
import "./UsersPage.css";

/* ------------------------------------------------------------------ */
/*  Wired to the RegisterAPIView you shared:                          */
/*    GET    /api/v1/user/          -> list (role in [0,2,3])         */
/*    GET    /api/v1/user/<id>/     -> single user                    */
/*    POST   /api/v1/user/          -> create (multipart, has photo)  */
/*    PUT    /api/v1/user/<id>/     -> update (multipart, has photo)  */
/*    DELETE /api/v1/user/<id>/     -> delete                         */
/*  See the "urls.py additions" note in the README for the two        */
/*  routes you need to add alongside your existing login/forgot ones. */
/* ------------------------------------------------------------------ */
const USERS_ENDPOINT = "/api/v1/register/";

const ROLE_LABELS = {
  0: "Admin",
  2: "Manager",
  3: "Staff",
};

// Roles that cannot open the Users page at all — matches the
// restriction already applied to the sidebar in DashboardLayout.
const RESTRICTED_ROLES = [3];

export default function UsersPage() {
  const { user } = useAuth();
  const isRestricted = RESTRICTED_ROLES.includes(user?.role);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // null => create mode
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [toast, setToast] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const { data } = await axiosClient.get(USERS_ENDPOINT);
      setUsers(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Couldn't load users. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isRestricted) return; // don't even fetch data for a blocked role
    fetchUsers();
  }, [fetchUsers, isRestricted]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  function openCreate() {
    setEditingUser(null);
    setModalOpen(true);
  }

  function openEdit(user) {
    setEditingUser(user);
    setModalOpen(true);
  }

  async function handleSaved(message) {
    setModalOpen(false);
    setToast(message);
    await fetchUsers();
  }

  async function confirmDelete() {
    if (!deletingUser) return;
    setDeleteBusy(true);
    try {
      await axiosClient.delete(`${USERS_ENDPOINT}${deletingUser.id}/`);
      setToast("User deleted.");
      setDeletingUser(null);
      await fetchUsers();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Delete failed. Please try again.");
    } finally {
      setDeleteBusy(false);
    }
  }

  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      u.employeeName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.toLowerCase?.().includes(q)
    );
  });

  // Staff (role 3) can't see this page at all.
  if (isRestricted) {
    return (
      <div className="upage">
        <div className="upage__banner upage__banner--error">
          You cannot access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="upage">
      <div className="upage__head">
        <div>
          <p className="upage__eyebrow">Team</p>
          <h1 className="upage__title">Users</h1>
        </div>
        <button className="upage__add-btn" onClick={openCreate}>+ Add user</button>
      </div>

      <div className="upage__toolbar">
        <input
          className="upage__search"
          placeholder="Search by name, email or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="upage__count">{filtered.length} of {users.length}</span>
      </div>

      {errorMsg && <div className="upage__banner upage__banner--error">{errorMsg}</div>}
      {toast && <div className="upage__banner upage__banner--ok">{toast}</div>}

      <div className="upage__table-wrap">
        <table className="upage__table">
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="upage__state">Loading users…</td></tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="upage__state">
                {users.length === 0 ? "No users yet — add your first one." : "No users match your search."}
              </td></tr>
            )}

            {!loading && filtered.map((u) => (
              <tr key={u.id}>
                <td>{u.row}</td>
                <td>
                  <div className="upage__user-cell">
                    {u.photo ? (
                      <img src={u.photo} alt="" className="upage__avatar" />
                    ) : (
                      <span className="upage__avatar upage__avatar--fallback">
                        {(u.employeeName || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span>{u.employeeName}</span>
                  </div>
                </td>
                <td>{u.email}</td>
                <td>{u.phone}</td>
                <td>
                  <span className="upage__pill">{ROLE_LABELS[u.role] ?? `Role ${u.role}`}</span>
                </td>
                <td>
                  <span className={`upage__status upage__status--${u.status ? "active" : "inactive"}`}>
                    {u.status ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="upage__actions">
                  <button className="upage__icon-btn" onClick={() => openEdit(u)}>Edit</button>
                  <button className="upage__icon-btn upage__icon-btn--danger" onClick={() => setDeletingUser(u)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <UserFormModal
          user={editingUser}
          endpoint={USERS_ENDPOINT}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {deletingUser && (
        <div className="upage__confirm-overlay" onMouseDown={(e) => e.target === e.currentTarget && setDeletingUser(null)}>
          <div className="upage__confirm-card">
            <h3>Delete {deletingUser.employeeName}?</h3>
            <p>This removes their login and profile. This can't be undone.</p>
            <div className="upage__confirm-actions">
              <button className="upage__btn-ghost" onClick={() => setDeletingUser(null)} disabled={deleteBusy}>Cancel</button>
              <button className="upage__btn-danger" onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}